import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Card } from '@/components/Card';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { Plus, Search, Database, Settings, Trash2, Upload } from 'lucide-react';
import {
  customObjectsService,
  CustomObjectType,
  CustomObjectRecord,
} from '@/services/custom-objects.service';
import { parseCsvToRows } from '@/lib/csv';
import toast from 'react-hot-toast';

function formatValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) return v.join(', ');
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

export default function CustomObjectRecordsList() {
  const { typeKey } = useParams<{ typeKey: string }>();
  const nav = useNavigate();
  // Contact/Lead detail pages link here with ?contactUid= / ?leadUid= to show
  // only the records linked to that entity.
  const [searchParams, setSearchParams] = useSearchParams();
  const filterContactUid = searchParams.get('contactUid') ?? undefined;
  const filterLeadUid = searchParams.get('leadUid') ?? undefined;
  const [type, setType] = useState<CustomObjectType | null>(null);
  const [records, setRecords] = useState<CustomObjectRecord[]>([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  // CSV import state
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importPreview, setImportPreview] = useState<{ headers: string[]; rows: Array<Record<string, string | undefined>> } | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ inserted: number; skipped: number; errors: Array<{ row: number; error: string }> } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const refresh = async () => {
    if (!typeKey) return;
    const [t, r] = await Promise.all([
      customObjectsService.getType(typeKey),
      customObjectsService.listRecords({
        typeKey,
        q: q || undefined,
        contactUid: filterContactUid,
        leadUid: filterLeadUid,
        limit: 100,
      }),
    ]);
    setType(t);
    setRecords(r.items);
  };

  useEffect(() => {
    setLoading(true);
    refresh().catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeKey, q, filterContactUid, filterLeadUid]);

  const previewFields = useMemo(() => {
    if (!type) return [];
    return type.fields.slice(0, 4);
  }, [type]);

  const onPickFile = async (file: File) => {
    setImportFile(file);
    setImportResult(null);
    try {
      const text = await file.text();
      const parsed = parseCsvToRows(text);
      setImportPreview(parsed);
    } catch {
      toast.error('Could not parse CSV');
      setImportPreview(null);
    }
  };

  const runImport = async () => {
    if (!type || !importPreview) return;
    setImporting(true);
    try {
      const fieldKeys = new Set(type.fields.map((f) => f.key));
      const rows = importPreview.rows.map((row) => {
        const values: Record<string, unknown> = {};
        for (const key of Object.keys(row)) {
          if (fieldKeys.has(key)) values[key] = row[key];
        }
        return {
          title: row.title ?? row.name ?? undefined,
          contactUid: row.contactUid ?? row.contactuid ?? undefined,
          values,
        };
      });
      const r = await customObjectsService.importRecords(type.key, rows);
      setImportResult(r);
      if (r.inserted > 0) toast.success(`Imported ${r.inserted} record${r.inserted === 1 ? '' : 's'}`);
      if (r.errors.length > 0) toast(`Skipped ${r.errors.length} row${r.errors.length === 1 ? '' : 's'} — see errors`, { icon: '⚠️' });
      await refresh();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg ?? 'Import failed');
    } finally { setImporting(false); }
  };

  const closeImport = () => {
    setImportOpen(false);
    setImportFile(null);
    setImportPreview(null);
    setImportResult(null);
  };

  const onDelete = async (recordUid: string) => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm('Delete this record?')) return;
    try {
      await customObjectsService.deleteRecord(recordUid);
      toast.success('Deleted');
      await refresh();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <>
      <Topbar
        title={type ? type.pluralLabel ?? type.label : (typeKey ?? 'Records')}
        breadcrumb="Data"
        description={type?.description}
      />

      <div className="px-7 py-7 w-full space-y-4">
        <Card padded>
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <Input
                placeholder="Search by title…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                iconLeft={<Search size={14} />}
              />
            </div>
            <Link
              to={`/custom-objects/types/${typeKey}`}
              className="rounded-lg border border-border px-2.5 py-2 text-xs hover:bg-surface-alt inline-flex items-center gap-1.5"
              title="Edit object type"
            >
              <Settings size={13} /> Edit type
            </Link>
            <Button
              size="sm"
              variant="secondary"
              iconLeft={<Upload size={14} />}
              onClick={() => setImportOpen(true)}
            >
              Import CSV
            </Button>
            <Button size="sm" iconLeft={<Plus size={14} />} onClick={() => nav(`/custom-objects/${typeKey}/new`)}>
              New {type?.label ?? 'record'}
            </Button>
          </div>
          {(filterContactUid || filterLeadUid) && (
            <div className="mt-2 flex items-center gap-2 text-xs text-text-muted">
              <span>
                Showing records linked to {filterContactUid ? 'a contact' : 'a lead'}.
              </span>
              <button
                type="button"
                onClick={() => setSearchParams({}, { replace: true })}
                className="font-semibold text-primary hover:underline"
              >
                Show all
              </button>
            </div>
          )}
        </Card>

        <Card padded>
          {loading ? (
            <div className="space-y-2">
              {[0, 1, 2, 3].map((i) => <div key={i} className="skeleton h-12 rounded-lg" />)}
            </div>
          ) : records.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-surface-alt/40 py-10 px-6 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-soft text-primary shadow-soft">
                <Database size={20} />
              </div>
              <p className="text-sm font-semibold text-text-primary">
                {q ? `No ${type?.label?.toLowerCase() ?? 'records'} match "${q}"` : `No ${type?.label?.toLowerCase() ?? 'records'} yet`}
              </p>
              {!q && (
                <>
                  <p className="mx-auto mt-1 max-w-md text-xs text-text-muted">
                    Add records here so the AI bot can answer customer questions about them — and so they show up
                    on contact detail pages and in Custom Objects&apos; AI context.
                  </p>
                  <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                    <Button size="sm" variant="primary" onClick={() => nav(`/custom-objects/${typeKey}/new`)}>
                      Add the first {type?.label ?? 'record'}
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => setImportOpen(true)}>
                      Import CSV
                    </Button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full text-sm">
                <thead className="bg-surface-alt/60 text-text-muted">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Title</th>
                    {previewFields.map((f) => (
                      <th key={f.key} className="px-3 py-2 text-left font-medium">{f.label}</th>
                    ))}
                    <th className="px-3 py-2 text-right font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((r) => (
                    <tr key={r.recordUid} className="border-t border-border hover:bg-surface-alt/40">
                      <td className="px-3 py-2">
                        <Link to={`/custom-objects/${typeKey}/${r.recordUid}`} className="font-medium text-text-primary hover:text-primary">
                          {r.title ?? <span className="text-text-muted italic">untitled</span>}
                        </Link>
                      </td>
                      {previewFields.map((f) => (
                        <td key={f.key} className="px-3 py-2 text-text-secondary truncate max-w-xs">
                          {formatValue(r.values?.[f.key])}
                        </td>
                      ))}
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => onDelete(r.recordUid)}
                          className="rounded-lg p-1.5 text-text-muted hover:bg-danger-soft hover:text-danger"
                          title="Delete"
                        >
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <Modal open={importOpen} onClose={closeImport} title={`Import ${type?.pluralLabel ?? 'records'} from CSV`} widthClass="max-w-2xl">
        <div className="px-6 py-5 space-y-4">
          <div className="rounded-xl border border-info/30 bg-info/5 p-3 text-xs text-text-secondary">
            <strong>CSV format:</strong> first row must contain field keys. Available keys for <code className="font-mono">{type?.key}</code>:
            <div className="mt-1.5 flex flex-wrap gap-1">
              {(type?.fields ?? []).map((f) => (
                <code key={f.key} className="rounded bg-surface-alt px-1.5 py-0.5 font-mono text-2xs">
                  {f.key}{f.required ? '*' : ''}
                </code>
              ))}
            </div>
            <div className="mt-1.5 text-2xs text-text-muted">
              Optional column <code className="font-mono">title</code> sets the record title (auto-derived if omitted).
              Optional <code className="font-mono">contactUid</code> links each row to an existing contact.
            </div>
          </div>

          {!importPreview && (
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onPickFile(f);
                }}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex w-full flex-col items-center gap-2 rounded-xl border border-dashed border-border bg-surface-alt/40 py-10 text-text-muted hover:border-primary/40 hover:bg-surface-alt"
              >
                <Upload size={28} className="opacity-60" />
                <span className="text-sm font-medium">Click to choose a CSV file</span>
              </button>
            </div>
          )}

          {importPreview && (
            <>
              <div className="rounded-xl border border-border">
                <div className="border-b border-border bg-surface-alt/50 px-3 py-2 text-xs">
                  Preview · {importPreview.rows.length} row{importPreview.rows.length === 1 ? '' : 's'}
                  {importFile && <span className="ml-2 text-text-muted">{importFile.name}</span>}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-surface-alt/40 text-text-muted">
                      <tr>
                        {importPreview.headers.map((h) => (
                          <th key={h} className="px-2 py-1.5 text-left font-medium">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.rows.slice(0, 5).map((row, i) => (
                        <tr key={i} className="border-t border-border">
                          {importPreview.headers.map((h) => (
                            <td key={h} className="px-2 py-1.5 text-text-secondary truncate max-w-xs">
                              {row[h] ?? <span className="text-text-muted italic">empty</span>}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {importPreview.rows.length > 5 && (
                    <div className="border-t border-border px-3 py-1.5 text-2xs text-text-muted">
                      … and {importPreview.rows.length - 5} more row{importPreview.rows.length - 5 === 1 ? '' : 's'}
                    </div>
                  )}
                </div>
              </div>

              {importResult && (
                <div className="rounded-xl border border-border bg-surface-alt/40 p-3 text-xs space-y-1">
                  <div className="font-semibold text-text-primary">
                    Inserted {importResult.inserted} · Skipped {importResult.skipped}
                  </div>
                  {importResult.errors.length > 0 && (
                    <ul className="space-y-0.5 text-warning">
                      {importResult.errors.slice(0, 10).map((e, i) => (
                        <li key={i}>Row {e.row}: {e.error}</li>
                      ))}
                      {importResult.errors.length > 10 && (
                        <li className="text-text-muted italic">… and {importResult.errors.length - 10} more</li>
                      )}
                    </ul>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-border px-6 py-4">
          {importPreview && !importResult && (
            <Button variant="secondary" onClick={() => { setImportPreview(null); setImportFile(null); }} disabled={importing}>
              Choose another file
            </Button>
          )}
          <Button variant="secondary" onClick={closeImport} disabled={importing}>
            {importResult ? 'Done' : 'Cancel'}
          </Button>
          {importPreview && !importResult && (
            <Button onClick={runImport} loading={importing}>
              Import {importPreview.rows.length} row{importPreview.rows.length === 1 ? '' : 's'}
            </Button>
          )}
        </div>
      </Modal>
    </>
  );
}
